/* eslint-disable operator-linebreak */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Scrollbars } from 'react-custom-scrollbars';
import classNames from 'classnames';

import {
  DRAGGABLE_DIRECTIONS,
  DRAGGABLE_DESTINATION_CHARGERS,
  TSLA_HEADER_MAIN,
  VEHICLE_SELECTOR,
  PLACES,
  ROUTE_SUMMARY_HEIGHT,
} from '../../lib/Constants';

// components
import DirectionsList from './list';
import DestinationChargers from '../DestinationChargers';
import Btn from '../../components/Btn';

// actions
import {
  createErrorMsgError,
  deleteErrorMessages,
} from '../../reducers/ErrorMsg/actions';
import { toggleEditPlaces } from '../../reducers/Places/actions';
import { bindDirectionsDraggable } from '../../reducers/Directions/actions';
import { toggleDestinationChargersUI } from '../../reducers/DestinationChargers/actions';

// helpers
import { i18n, getNodeHeight, getComponentHeight } from '../../lib/Utils';
import MapMarkers from '../../lib/MapMarkers';
import MapWindow from '../../lib/MapWindow';
import Enliven from '../../lib/Enliven';

const DirectionsPropTypes = {
  tripIsValid: PropTypes.bool, // If the min requirements for a trip have been met
  directionsIsLoading: PropTypes.bool.isRequired, // If the min requirements for a trip have been met
  isMobile: PropTypes.bool.isRequired, // Is this a mobile device
  directionsLoaded: PropTypes.bool.isRequired, // If the directions have successfully returned from API
  directionsError: PropTypes.bool.isRequired, // True if an error occurred
  dispatchCreateErrorMsgError: PropTypes.func.isRequired, // Dispatches a friendly message to the store
  dispatchToggleEditPlaces: PropTypes.func.isRequired, // Toggle Places edit mode
  invalidTrip: PropTypes.string.isRequired, // Error message for an impossible trip
  fields: PropTypes.arrayOf(PropTypes.object).isRequired, // Contains objects with information about each input field & location
  showDestinationChargers: PropTypes.bool.isRequired, // True if destination chargers are displayed
  animationName: PropTypes.string.isRequired, // Name of the animation applied to elements
  translateX: PropTypes.string.isRequired, // translateX CSS property for animation
  animationTimeout: PropTypes.number.isRequired, // duration of animation
  dispatchDirectionsBindDraggability: PropTypes.func.isRequired, // Lets app know drag is bound
  draggable: PropTypes.bool.isRequired, // True when Directions is draggable
  btnLabel: PropTypes.string.isRequired, // Label for 'Back to Route' button
  destinationChargers: PropTypes.arrayOf(PropTypes.object).isRequired, // Collection of data about destination chargers
  windowHeight: PropTypes.number.isRequired, // Height of the users screen
  dispatchDeleteErrorMessages: PropTypes.func.isRequired, // Removes all error messages from Redux store
  dispatchToggleDestinationChargersUI: PropTypes.func.isRequired, // Toggle destination charger UI
  componentHeights: PropTypes.arrayOf(PropTypes.object).isRequired, // component heights
};

const DirectionsDefaultProps = {
  tripIsValid: false,
};

class Directions extends Component {
  constructor(props) {
    super(props);

    this.toggleChargerList = this.toggleChargerList.bind(this);
    this.initDraggableDirections = this.initDraggableDirections.bind(this);
  }

  componentDidUpdate(prevProps) {
    const {
      isMobile,
      directionsLoaded,
      directionsError,
      fields,
      invalidTrip,
      draggable,
      dispatchDeleteErrorMessages,
      dispatchCreateErrorMsgError,
      dispatchToggleEditPlaces,
    } = this.props;

    const { initDraggableDirections } = this;

    if (directionsLoaded && isMobile && !draggable) initDraggableDirections();

    try {
      if (
        fields[0].address !== prevProps.fields[0].address ||
        fields[fields.length - 1].address !==
          prevProps.fields[fields.length - 1].address
      ) {
        dispatchDeleteErrorMessages();
      }
    } catch (e) {
      //
    }

    if (directionsError && directionsError !== prevProps.directionsError) {
      dispatchCreateErrorMsgError(
        i18n(invalidTrip, {
          ORIGIN: fields[0].address,
          DESTINATION: fields[fields.length - 1].address,
        }),
      );
      dispatchToggleEditPlaces(true);
    }
  }

  /**
   * @description Calculates the height of Directions component
   *
   * @return {integer} scrollable height
   */
  getScrollableHeight() {
    const { windowHeight, componentHeights } = this.props;
    const header = getNodeHeight(TSLA_HEADER_MAIN);
    return (
      windowHeight -
      header -
      getComponentHeight({
        components: componentHeights,
        component: VEHICLE_SELECTOR,
      }) -
      getComponentHeight({ components: componentHeights, component: PLACES }) -
      ROUTE_SUMMARY_HEIGHT
    );
  }

  toggleChargerList() {
    const {
      isMobile,
      destinationChargers,
      showDestinationChargers,
      dispatchToggleDestinationChargersUI,
    } = this.props;

    // close the info window
    MapWindow.closeInfoWindow(isMobile);

    // animate draggable destination chargers component
    const ddcCurrentY = Enliven.getTranslateY(DRAGGABLE_DESTINATION_CHARGERS);
    Enliven.animate(
      DRAGGABLE_DESTINATION_CHARGERS,
      'ddc-slide-right',
      '-100%',
      0,
      ddcCurrentY,
      ddcCurrentY,
      'down',
    );

    // animate draggable directions component
    const ddCurrentY = Enliven.getTranslateY(DRAGGABLE_DIRECTIONS);
    Enliven.animate(
      DRAGGABLE_DIRECTIONS,
      'slide-right',
      '-100%',
      0,
      ddCurrentY,
      ddCurrentY,
      'down',
    );

    // permanently delete map markers
    MapMarkers.deleteMapMarkers(destinationChargers, true);

    dispatchToggleDestinationChargersUI(!showDestinationChargers);
  }

  initDraggableDirections() {
    const { dispatchDirectionsBindDraggability } = this.props;
    Enliven.init({ id: DRAGGABLE_DIRECTIONS });
    Enliven.init({ id: DRAGGABLE_DESTINATION_CHARGERS });
    dispatchDirectionsBindDraggability(true);
  }

  // TODO: decouple component since it returns JSX
  renderScrollbars() {
    const { animationName, translateX, animationTimeout } = this.props;
    const scrollableHeight = this.getScrollableHeight();

    // apply animations to scrollbar component for slide left/right effect
    let styles = { height: scrollableHeight, zIndex: 1, width: '370px' };
    if (animationName) {
      styles = Object.assign({}, styles, {
        transform: `${translateX}`,
        animation: `${animationName} .${animationTimeout}s 1`,
      });
    }

    return (
      <div>
        <Scrollbars className={classNames('scrollbars')} style={styles}>
          <ol className={classNames('directions')}>
            <DirectionsList />
          </ol>
        </Scrollbars>
        <DestinationChargers />
      </div>
    );
  }

  // TODO: comment
  // TODO: decouple component since it returns JSX
  renderDraggable() {
    const {
      isMobile,
      directionsLoaded,
      btnLabel,
      showDestinationChargers,
    } = this.props;

    return (
      <div>
        <div
          id="draggable-directions"
          className={classNames({
            'directions--mobile': isMobile && directionsLoaded,
          })}
          role="switch"
          aria-checked="false"
          tabIndex="0"
        >
          <div className="icon-container">
            <i className="tsla-icon-angle_up" />
          </div>
          <ol className="directions">
            <DirectionsList />
          </ol>
        </div>
        <Btn
          styles={classNames('destination-chargers__btn', {
            'places--slide-up': !showDestinationChargers,
            'places--slide-down': showDestinationChargers,
          })}
          handleClick={this.toggleChargerList}
        >
          <i className="tsla-icon-angle_left" />
          {btnLabel}
        </Btn>
        <div id="draggable-destination-chargers">
          <DestinationChargers />
        </div>
      </div>
    );
  }

  render() {
    const {
      directionsIsLoading,
      tripIsValid,
      isMobile,
      directionsLoaded,
    } = this.props;

    if (directionsIsLoading) {
      return <div className="section-loader" />;
    }

    if (isMobile && directionsLoaded) {
      return this.renderDraggable();
    }

    if (tripIsValid && directionsLoaded) {
      return this.renderScrollbars();
    }

    return null;
  }
}

Directions.propTypes = DirectionsPropTypes;
Directions.defaultProps = DirectionsDefaultProps;

const mapStateToProps = state => ({
  tripIsValid: state.Places.tripIsValid,
  directionsIsLoading: state.Directions.directionsIsLoading,
  directionsLoaded: state.Directions.directionsLoaded,
  isMobile: state.App.isMobile,
  directionsError: state.Directions.directionsError,
  invalidTrip: state.ErrorMsg.errors.invalidTrip,
  fields: state.Places.fields,
  showDestinationChargers: state.DestinationChargers.showDestinationChargers,
  animationName: state.DestinationChargers.animationName,
  translateX: state.DestinationChargers.translateX,
  animationTimeout: state.DestinationChargers.animationTimeout,
  draggable: state.Directions.draggable,
  btnLabel: state.DestinationChargers.btnLabel,
  destinationChargers: state.DestinationChargers.destinationChargers,
  windowHeight: state.App.windowHeight,
  componentHeights: state.App.componentHeights,
});

const mapDispatchToProps = dispatch => ({
  // eslint-disable-next-line arrow-parens
  dispatchCreateErrorMsgError: errorMsg => {
    dispatch(createErrorMsgError(errorMsg));
  },
  // eslint-disable-next-line arrow-parens
  dispatchToggleEditPlaces: editing => {
    dispatch(toggleEditPlaces(editing));
  },
  // eslint-disable-next-line arrow-parens
  dispatchDirectionsBindDraggability: draggable => {
    dispatch(bindDirectionsDraggable(draggable));
  },
  dispatchDeleteErrorMessages: () => {
    dispatch(deleteErrorMessages());
  },
  // eslint-disable-next-line arrow-parens
  dispatchToggleDestinationChargersUI: showDestinationChargers => {
    dispatch(toggleDestinationChargersUI(showDestinationChargers));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Directions);
